import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calculator, ChevronDown, ChevronUp, RefreshCw, Lock } from "lucide-react";

const OUT_CLS = (label) =>
  /high|strong/i.test(label) ? "bg-teal-600 text-white border-teal-600"
    : /moderate/i.test(label) ? "bg-teal-50 text-teal-700 border-teal-300"
    : /contested|low|limited|isolated/i.test(label) ? "bg-amber-50 text-amber-700 border-amber-300"
    : "bg-sky-50 text-sky-700 border-sky-300";

function ScoreRow({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm" data-testid={`score-${r.score_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] text-slate-400">{r.score_id}</span>
        <span className="text-xs font-bold text-slate-800 flex-1 min-w-[220px] leading-snug">{r.subject}</span>
        {r.output.type === "numeric"
          ? <span className="font-mono text-sm font-bold text-teal-700">{r.output.value}<span className="text-slate-400 font-normal">/100</span></span>
          : <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-600 border-indigo-200">Categorical</Badge>}
        <Badge variant="outline" className={`text-[9px] ${OUT_CLS(r.output.label)}`} data-testid={`score-label-${r.score_id}`}>{r.output.label}</Badge>
        {!r.denominator.sufficient && <span className="text-[9px] text-amber-600 font-mono">n={r.denominator.n} &lt; {r.denominator.threshold}</span>}
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {r.output.basis && <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{r.output.basis}</p>}
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[9px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-1.5 pr-3">Component</th><th className="py-1.5 pr-3 text-right">Value</th>
                <th className="py-1.5 pr-3 text-right">Weight</th><th className="py-1.5">Basis</th>
              </tr>
            </thead>
            <tbody>
              {r.components.map((c) => (
                <tr key={c.name} className="border-b border-slate-100 align-top">
                  <td className="py-1.5 pr-3 font-semibold text-slate-700">{c.name}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-teal-700">{c.value}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-slate-500">{Math.round(c.weight * 100)}%</td>
                  <td className="py-1.5 text-slate-500 leading-snug">{c.basis}</td>
                </tr>
              ))}
              <tr><td className="py-1.5 pr-3 text-[10px] text-slate-400">Weights sum</td><td></td>
                <td className="py-1.5 pr-3 text-right font-mono text-[10px] text-slate-400">{Math.round(r.weights_sum * 100)}%</td><td></td></tr>
            </tbody>
          </table>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10.5px]">
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400">Denominator</p>
              <p className="text-slate-700">n = {r.denominator.n} {r.denominator.unit} · threshold {r.denominator.threshold} · {r.denominator.sufficient ? "sufficient for numeric output" : "below numeric threshold — categorical output"}</p>
              <p className="text-slate-400 mt-0.5">{r.denominator.description}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400">Missing inputs</p>
              {r.missing_inputs.length ? r.missing_inputs.map((m) => <p key={m} className="text-amber-700">• {m}</p>) : <p className="text-slate-500">None</p>}
            </div>
          </div>
          {r.adjustments?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[10.5px]" data-testid={`adjustments-${r.score_id}`}>
              <p className="font-mono text-[8.5px] uppercase tracking-widest text-amber-600">Adjustments — anti-inflation de-duplication</p>
              {r.adjustments.map((a) => <p key={a} className="text-amber-800 mt-0.5">• {a}</p>)}
            </div>
          )}
          {r.cap && <p className="text-[10px] text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3 text-slate-400" />Cap {r.cap.limit}{r.cap.applied ? " applied — " : " (not triggered) — "}{r.cap.reason}</p>}
          <p className="font-mono text-[9px] text-slate-400">{r.calculation_version} · cut-off {r.evidence_cutoff} · reviewer {r.reviewer}</p>
        </div>
      )}
    </div>
  );
}

export function ScoringService({ isReviewer }) {
  const [data, setData] = useState(null);
  const [family, setFamily] = useState("assessment_confidence");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/scoring").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-4 text-slate-400 text-sm" data-testid="scoring-loading">Loading scoring service…</div>;

  const recalc = async () => {
    setBusy(true);
    try {
      const r = await api.post("/scoring/recalculate");
      setData(r.data);
      toast.success(`Recalculated — run ${r.data.run.run_id} recorded (${r.data.calculation_version}).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recalculation failed.");
    } finally {
      setBusy(false);
    }
  };

  const scores = data.scores.filter((r) => r.family === family);
  return (
    <div className="bg-slate-50/60 border border-slate-200/80 rounded-xl p-5 space-y-4" data-testid="scoring-service">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-teal-600" />Unified scoring service
            <span className="font-mono text-[10px] text-slate-400 font-normal">{data.calculation_version} · cut-off {data.evidence_cutoff}</span>
          </h3>
          <p className="text-[11.5px] text-slate-600 mt-1 leading-relaxed max-w-3xl">{data.policy}</p>
          {data.last_run && <p className="font-mono text-[9px] text-slate-400 mt-1">Last recalculation: {data.last_run.run_id} · {data.last_run.run_at?.slice(0, 16).replace("T", " ")} UTC · {data.last_run.run_by}</p>}
        </div>
        {isReviewer && (
          <button onClick={recalc} disabled={busy} data-testid="recalculate-btn"
            className="flex items-center gap-1.5 text-[11px] font-semibold bg-slate-900 text-white rounded-full px-4 py-2 hover:bg-slate-700 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />{busy ? "Recalculating…" : "Recalculate"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {data.families.map((f) => (
          <button key={f.id} onClick={() => setFamily(f.id)} data-testid={`scoring-family-${f.id}`}
            className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border transition-colors ${family === f.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"}`}>
            {f.label} <span className="font-mono text-[9px] opacity-60">{f.count} · {f.output_mode}</span>
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="scoring-list">
        {scores.map((r) => <ScoreRow key={r.score_id} r={r} />)}
      </div>
    </div>
  );
}
